import { Grid, Typography, useTheme } from "@mui/material";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Suggestion } from "../Suggestions/Suggestion";
import { useStore } from "utils/useStore";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxButton from "components/UI/AcxButton";
import { useNavigate } from "react-router";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { kboFiltersToApplicationFilters } from "../../AgentUtils";
import RootStore from "stores/RootStore";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";
import DashboardStore from "components/Signals/Dashboard/DashboardStore";
import AcxMainTextField from "components/UI/AcxMainTextField";
import {
    ADDITIONAL_CHARTS_DASHBOARD,
    ISignalsChartDefinition,
    SignalsDashboardType,
    SignalsObjectType,
} from "components/Signals/SignalsModels";
import { SelectedCardRequestPayload } from "services/SignalsService";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";

export const AddPreExistingGraphToDashboard = observer(({ args, remove }) => {
    const navigate = useNavigate();
    const theme = useTheme();

    const signalsPageStore = useStore(SignalsPageStore);
    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const messageStore = useStore(MessageStore);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [selectedDashboard, setSelectedDashboard] =
        useState<DashboardStore>();
    const [newDashboardTitle, setNewDashboardTitle] = useState("");

    const [isAddLoading, setIsAddLoading] = useState(false);
    const dashboardsLoaded = signalsPageStore.allDashboards.size > 0;
    const dashboardsLoading = signalsPageStore.getTaskLoading("LoadDashboards");

    const isCreateDashboard = useMemo(
        () => !!newDashboardTitle.trim(),
        [newDashboardTitle],
    );

    const isFormComplete = useMemo(
        () => !!selectedDashboard || isCreateDashboard,
        [selectedDashboard, isCreateDashboard],
    );

    useEffect(() => {
        if (dashboardsLoaded || dashboardsLoading) return;
        signalsPageStore.loadDashboards();
    }, [dashboardsLoaded, dashboardsLoading, signalsPageStore]);

    const dashboardOptions = useMemo(() => {
        return signalsPageStore.allDashboardsArray.filter((dashboard) => {
            if (dashboard.dashboardType !== SignalsDashboardType.Custom)
                return false;
            if (!!dashboard.pinnedApplicationFilterId) return false;
            if (!dashboard.dashboardIsEditable) return false;
            if (dashboard.signalsChartCount >= 20) return false;
            return true;
        });
    }, [signalsPageStore.allDashboardsArray]);

    const chartDefinition = useMemo(() => {
        const dashboardsToSearch = [
            ...signalsPageStore.standardDashboards,
            ...signalsPageStore.allDashboardsArray.filter(
                (dash) => dash.title === ADDITIONAL_CHARTS_DASHBOARD,
            ),
        ];

        for (const dashboard of dashboardsToSearch) {
            const chart = dashboard.signalsObjects.find(
                (obj) =>
                    obj.objectType === SignalsObjectType.Chart &&
                    obj.dataEndpoint === args.chartId,
            );
            if (chart) return chart as ISignalsChartDefinition;
        }

        return undefined;
    }, [
        args.chartId,
        signalsPageStore.allDashboardsArray,
        signalsPageStore.standardDashboards,
    ]);
    const isAddEnabled = isFormComplete && !!chartDefinition;

    const addToDashboard = useCallback(async () => {
        let dashboard: DashboardStore | null | undefined;

        if (isCreateDashboard) {
            dashboard = await signalsPageStore.createDashboard({
                title: newDashboardTitle.trim(),
            });
        } else {
            dashboard = selectedDashboard;
        }

        const dashboardId = dashboard?.id;
        if (!dashboardId) {
            messageStore.logError(
                "Could not find or create the specified dashboard.",
            );
            return false;
        }

        if (!chartDefinition) {
            messageStore.logError(
                "Unable to find the requested chart definition.",
            );
            return false;
        }

        const payload: SelectedCardRequestPayload = {
            type: SignalsObjectType.Chart,
            info: {
                dataEndpoint: chartDefinition.dataEndpoint,
                title: chartDefinition.title,
                subtitle: chartDefinition.subtitle,
                xTitle: chartDefinition.xTitle,
                yTitle: chartDefinition.yTitle,
                vizOptions: chartDefinition.vizOptions,
                typeOfChart: chartDefinition.typeOfChart,
                typeOfDataSelectedInChart:
                    chartDefinition.typeOfDataSelectedInChart,
                availableRenderModes: chartDefinition.availableRenderModes,
                defaultRenderMode: chartDefinition.defaultRenderMode,
                isPaginated: chartDefinition.isPaginated,
                compareToPreviousModeIsAvailable:
                    chartDefinition.compareToPreviousModeIsAvailable,
                isTemporal: chartDefinition.isTemporal,
                showDataGrid: chartDefinition.showDataGrid,
                showSpotlight: chartDefinition.showSpotlight,
                showAnalyzeWithKBo: chartDefinition.showAnalyzeWithKBo,
                objectFilters: chartDefinition.objectFilters,
                orderRuleId: chartDefinition.orderRuleId,
            },
        };

        try {
            if (dashboard) {
                if (!(await dashboard.addCards([payload]))) return false;
            } else {
                await signalsPageStore.signalsService.addSelectedCardsToDashboard(
                    dashboardId,
                    [payload],
                );
            }

            applicationFiltersStore.resetAllFilters();

            if (args.filters) {
                const filters = await kboFiltersToApplicationFilters(
                    args.filters,
                );
                applicationFiltersStore.applyExternalFilters(filters, true);
            }

            const dashboardUrlToNavigateTo = `/app/signals/dashboard/${dashboardId}`;
            const isAlreadyViewingDashboard =
                dashboardUrlToNavigateTo ===
                RootStore().activeLocation.location;

            if (isAlreadyViewingDashboard) {
                signalsPageStore.setCurrentDashboard({ dashboardId });
                await dashboard?.applyFilters?.();
            } else {
                navigate(dashboardUrlToNavigateTo);
            }

            remove();

            return true;
        } catch (error) {
            messageStore.logError("Failed to add chart to dashboard.");
            return false;
        }
    }, [
        args,
        applicationFiltersStore,
        chartDefinition,
        isCreateDashboard,
        messageStore,
        navigate,
        newDashboardTitle,
        remove,
        selectedDashboard,
        signalsPageStore,
    ]);

    if (!args.chartId || !args.filters) return null;

    return (
        <React.Fragment>
            <AcxDialog
                isOpen={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setSelectedDashboard(undefined);
                    setNewDashboardTitle("");
                }}
                title="Add to Dashboard"
                maxWidth="lg"
                dialogContentChildren={
                    <Grid container item direction="column" rowGap={3}>
                        <Grid item>
                            <Typography fontWeight="bold" fontSize="13px">
                                Select an existing dashboard
                            </Typography>
                            <AcxSelectSingle
                                id="kbo-add-graph-to-dashboard"
                                options={dashboardOptions}
                                labelField="title"
                                valueField="id"
                                isLoading={signalsPageStore.getTaskLoading(
                                    "LoadDashboards",
                                )}
                                isClearable
                                defaultValue={selectedDashboard}
                                onChange={(option) => {
                                    setSelectedDashboard(option || undefined);
                                    setNewDashboardTitle("");
                                }}
                                isDisabled={isCreateDashboard}
                                fullWidth
                            />
                        </Grid>
                        <Grid container item direction="column" rowGap={1}>
                            <Typography fontWeight="bold" fontSize="13px">
                                Or create a new dashboard
                            </Typography>
                            <Grid
                                container
                                style={{ width: "30vw" }}
                                spacing={1}
                            >
                                <Grid item xs={12}>
                                    <AcxMainTextField
                                        id="new-dashboard-title-input"
                                        labelText="Dashboard Name"
                                        value={newDashboardTitle}
                                        onChange={(e) => {
                                            if (selectedDashboard) {
                                                setSelectedDashboard(undefined);
                                            }
                                            setNewDashboardTitle(
                                                e.target.value,
                                            );
                                        }}
                                        required={!selectedDashboard}
                                        isDisabled={!!selectedDashboard}
                                        textContainerSx={{
                                            backgroundColor: selectedDashboard
                                                ? theme.palette.lightgray
                                                      .background
                                                : undefined,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            >
                <AcxButton
                    disabled={!isAddEnabled}
                    onClick={async () => {
                        setIsAddLoading(true);

                        const wasSuccessful = await addToDashboard();

                        if (wasSuccessful) {
                            setDialogOpen(false);
                            setSelectedDashboard(undefined);
                            setNewDashboardTitle("");
                        }

                        setIsAddLoading(false);
                    }}
                    loading={isAddLoading}
                >
                    Add
                </AcxButton>
            </AcxDialog>
            <Grid item>
                <Suggestion onClick={() => setDialogOpen((open) => !open)}>
                    {args?.buttonText || "Visualize this data over time"}
                </Suggestion>
            </Grid>
        </React.Fragment>
    );
});
